'use client'
import type { Address } from 'viem'

import { type ChainId, ChainUtils } from '@morpho-org/blue-sdk'
import { ClipboardButton } from '@morpho-org/ui-v6/client'
import { isZeroAddressOrUnset } from '@morpho-org/ui-v6/utils'
import { Icon } from '@repo/ui'
import { Alert, ArrowDiagonalUp } from '@repo/ui/icons'
import { AbbreviatedHex } from '@repo/web3'
import { memo, useMemo } from 'react'

import { AragonDaoLabel } from '@/components/common/AragonDaoLabel'
import { ButtonLink } from '@/components/common/ButtonLink'
import { SafeQuorum } from '@/components/common/SafeQuorum'

const AddressWithCopyAndExplorer = ({
  address,
  chainId,
  safeQuorum,
  isAragonDAO
}: {
  address: Address
  chainId: ChainId
  safeQuorum?: string
  isAragonDAO?: boolean
}) => {
  const explorerUrl = useMemo(
    () => ChainUtils.getExplorerAddressUrl(chainId, address),
    [address, chainId]
  )

  if (isZeroAddressOrUnset(address)) {
    return (
      <div className="gap-2xs flex items-center">
        <Icon className="text-secondary-foreground" icon={<Alert />} />
        <span className="text-foreground text-xs" style={{ lineHeight: 'normal' }}>
          No address
        </span>
      </div>
    )
  }

  return (
    <div className="gap-2xs flex cursor-default items-center" onClick={e => e.stopPropagation()}>
      {isAragonDAO ? (
        <AragonDaoLabel textClassName="text-xs" />
      ) : safeQuorum ? (
        <SafeQuorum safeQuorum={safeQuorum} textClassName="text-xs" />
      ) : (
        <AbbreviatedHex
          value={address}
          className="text-foreground text-xs"
          style={{ lineHeight: 'normal' }}
        />
      )}
      <ClipboardButton
        toCopy={address}
        toastMessage="Address copied to clipboard."
        tooltipMessage="Copy address."
        size="sm"
      />
      <ButtonLink
        href={explorerUrl}
        mode="external"
        buttonProps={{
          variant: 'ghost',
          size: 'sm',
          iconAlone: <ArrowDiagonalUp />,
          iconRight: undefined
        }}
      />
    </div>
  )
}

export default memo(AddressWithCopyAndExplorer)
